import { RequiredInfo, RequiredInfoValue } from '@/util/types/serverTypes';

/**
 * Single source of truth for "has the customer answered the required questions
 * for the services they picked?"
 *
 * Required info is configured per company (Settings → Required Service Info) and
 * attached to individual services (`service._requiredInfoId`). Items flagged
 * `required` are operational preconditions for the inspection — gate codes,
 * occupancy, utility status — so a job booked without them cannot be performed.
 *
 * Everything here is pure so it can be shared by the More Information step, the
 * step validators that gate navigation, and unit tests without a React tree.
 */

/** Minimal shape needed off a service — keeps this module decoupled from the
 *  various service view types (`Service`, `ServWithMods`, `ServiceWithPricing`). */
export type ServiceRequiredInfoRef = {
  _id: string;
  _requiredInfoId?: string[] | null;
};

export type RequiredInfoValidationInput = {
  /** Every service id in the cart: primary + addons + bundle items. */
  selectedServiceIds: readonly string[];
  /** The company's service catalog (only `_id` / `_requiredInfoId` are read). */
  services: readonly ServiceRequiredInfoRef[];
  /** The company's full required-info catalog. */
  allRequiredInfo: readonly RequiredInfo[];
  /** Answers collected so far. */
  values: readonly RequiredInfoValue[];
};

export type RequiredInfoValidationResult = {
  /** Ids of every required-info item attached to the selected services, required or not. */
  requiredInfoIds: string[];
  /** The items that must be answered before booking (`required` and not `hidden`). */
  requiredItems: RequiredInfo[];
  /** The subset of `requiredItems` that has no usable answer yet. */
  missingItems: RequiredInfo[];
  /** True when every required item has an answer. */
  isValid: boolean;
};

/**
 * An answer counts as present when it is a non-blank string. `'false'` and
 * `'0'` are real answers (boolean switches and number inputs store them), so
 * only null/undefined/whitespace is treated as unanswered.
 */
export const hasRequiredInfoAnswer = (
  value: RequiredInfoValue | undefined
): boolean => {
  if (!value) return false;
  return typeof value.value === 'string'
    ? value.value.trim() !== ''
    : value.value != null;
};

/**
 * Collects the required-info ids attached to the selected services, de-duplicated
 * and in catalog order. Unknown service ids are ignored — the catalog is the
 * authority on what a service asks for.
 */
export const resolveRequiredInfoIds = (
  selectedServiceIds: readonly string[],
  services: readonly ServiceRequiredInfoRef[]
): string[] => {
  if (!selectedServiceIds?.length || !services?.length) return [];

  const selected = new Set(selectedServiceIds);
  const ids = new Set<string>();

  services.forEach((service) => {
    if (!selected.has(service._id)) return;
    service._requiredInfoId?.forEach((id) => {
      if (id) ids.add(id);
    });
  });

  return Array.from(ids);
};

/** Sorts by the admin-configured display order so messages stay deterministic. */
const byDisplayOrder = (a: RequiredInfo, b: RequiredInfo) => {
  const orderDiff = (a.order ?? 9999) - (b.order ?? 9999);
  return orderDiff !== 0 ? orderDiff : a.name.localeCompare(b.name);
};

/**
 * Resolves which of a company's required-info items the customer must answer for
 * the services in their cart. Hidden items are excluded — they are populated by
 * staff after the fact and are never shown in the online scheduler.
 */
export const resolveRequiredItems = (
  requiredInfoIds: readonly string[],
  allRequiredInfo: readonly RequiredInfo[]
): RequiredInfo[] => {
  if (!requiredInfoIds?.length || !allRequiredInfo?.length) return [];

  const itemsById = new Map(allRequiredInfo.map((item) => [item._id, item]));

  return requiredInfoIds
    .map((id) => itemsById.get(id))
    .filter((item): item is RequiredInfo => !!item && item.required && !item.hidden)
    .sort(byDisplayOrder);
};

/**
 * The one function every caller should use. Safe to call with partially
 * populated inputs (empty catalog ⇒ nothing is required ⇒ valid).
 */
export const validateRequiredInfo = ({
  selectedServiceIds,
  services,
  allRequiredInfo,
  values,
}: RequiredInfoValidationInput): RequiredInfoValidationResult => {
  const requiredInfoIds = resolveRequiredInfoIds(selectedServiceIds, services);
  const requiredItems = resolveRequiredItems(requiredInfoIds, allRequiredInfo);

  const valuesById = new Map(
    (values ?? []).map((value) => [value._requiredInfoId, value])
  );

  const missingItems = requiredItems.filter(
    (item) => !hasRequiredInfoAnswer(valuesById.get(item._id))
  );

  return {
    requiredInfoIds,
    requiredItems,
    missingItems,
    isValid: missingItems.length === 0,
  };
};

/**
 * Human-readable labels for the missing items, used in stepper tooltips and
 * validation summaries. Falls back to a generic line when the item names would
 * make the list unhelpfully long.
 */
export const describeMissingRequiredInfo = (
  missingItems: readonly RequiredInfo[],
  maxNames = 5
): string[] => {
  if (!missingItems.length) return [];
  if (missingItems.length > maxNames) {
    return [`${missingItems.length} required questions still need answers`];
  }
  return missingItems.map((item) => item.name);
};
